/*
 * Miroir de `Password::defaults()` côté PASS (AppServiceProvider) : 12 caractères, une
 * majuscule, une minuscule, un chiffre et un caractère spécial.
 *
 * PASS reste l'autorité — cette copie sert à énoncer la règle avant la saisie et à éviter
 * un aller-retour. Elle vit à un seul endroit parce que les deux formulaires divergeaient :
 * « Changer mon mot de passe » contrôlait les cinq règles, tandis que « Nouveau mot de
 * passe » n'en contrôlait aucune et en affichait une liste amputée du caractère spécial —
 * l'utilisateur suivait les règles à l'écran et se faisait refuser (AMODEV-32888).
 *
 * Les classes Unicode reprennent celles de Laravel (\p{N} pour les chiffres, \p{Z}\p{S}\p{P}
 * pour les caractères spéciaux) : un « é » n'est donc pas un caractère spécial ici non plus.
 */
export const PASSWORD_MIN_LENGTH = 12

interface PasswordRule {
    label: string
    error: string
    satisfiedBy: (password: string) => boolean
}

const RULES: PasswordRule[] = [
    {
        label: `Au moins ${PASSWORD_MIN_LENGTH} caractères`,
        error: `Le mot de passe doit contenir ${PASSWORD_MIN_LENGTH} caractères minimum`,
        satisfiedBy: (password) => password.length >= PASSWORD_MIN_LENGTH
    },
    {
        label: 'Au moins une majuscule',
        error: 'Le mot de passe doit contenir au moins une majuscule',
        satisfiedBy: (password) => /\p{Lu}/u.test(password)
    },
    {
        label: 'Au moins une minuscule',
        error: 'Le mot de passe doit contenir au moins une minuscule',
        satisfiedBy: (password) => /\p{Ll}/u.test(password)
    },
    {
        label: 'Au moins un chiffre',
        error: 'Le mot de passe doit contenir au moins un chiffre',
        satisfiedBy: (password) => /\p{N}/u.test(password)
    },
    {
        label: 'Au moins un caractère spécial',
        error: 'Le mot de passe doit contenir au moins un caractère spécial',
        satisfiedBy: (password) => /[\p{Z}\p{S}\p{P}]/u.test(password)
    },
    /*
     * Hotfix temporaire : le caractère '&' provoque un problème d'encodage form-urlencoded
     * côté Gravitee AM et casse silencieusement l'auth. À retirer une fois le fix côté
     * Gravitee déployé — la règle est ici pour être aussi affichée à l'utilisateur.
     */
    {
        label: 'Pas de caractère « & »',
        error: "Le caractère « & » n'est temporairement pas accepté. Merci d'utiliser un autre caractère spécial.",
        satisfiedBy: (password) => !password.includes('&')
    }
]

/** Libellés des règles, dans l'ordre, pour les afficher à côté du champ. */
export const PASSWORD_RULES = RULES.map(rule => rule.label)

/** Message de la première règle non respectée, ou `null` si le mot de passe convient. */
export const firstPasswordRuleError = (password: string): string | null => RULES.find(rule => !rule.satisfiedBy(password))?.error ?? null
